import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, LoginCredentials, AuthResponse } from '../types';
import apiClient, { API_ENDPOINTS } from '../config/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Development bypass flag
  const bypassAuth = import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true';

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (bypassAuth) {
        // In dev mode with bypass, create mock user
        setUser({
          username: 'admin',
          is_admin: true,
          is_active: true,
          email: 'admin@dev.local'
        });
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await apiClient.get(API_ENDPOINTS.AUTH.ME);
          setUser(response.data);
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [bypassAuth]);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    if (bypassAuth) {
      // Dev bypass: accept any credentials
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate delay

      setUser({
        username: credentials.username,
        is_admin: true,
        is_active: true,
        email: `${credentials.username}@dev.local`
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      console.log('🔐 Sending login request to:', API_ENDPOINTS.AUTH.LOGIN);

      // Backend expects JSON
      const response = await apiClient.post<AuthResponse>(
        API_ENDPOINTS.AUTH.LOGIN,
        {
          username: credentials.username,
          password: credentials.password,
        }
      );

      console.log('✅ Login response received');

      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      console.log('🔑 Token saved to localStorage');

      // Get user info
      console.log('👤 Fetching user info...');
      const userResponse = await apiClient.get(API_ENDPOINTS.AUTH.ME);
      console.log('👤 User info received:', userResponse.data);
      setUser(userResponse.data);
    } catch (error: any) {
      console.error('❌ Login error:', error);
      console.error('❌ Error response:', error.response?.data);
      throw new Error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const refreshToken = async (): Promise<void> => {
    try {
      const response = await apiClient.post<AuthResponse>(API_ENDPOINTS.AUTH.REFRESH);
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
